import type { NurseryOrderLine } from '../../types/nursery';
import PbrMark from '../cultivar/PbrMark';
import PbrNotice from '../cultivar/PbrNotice';
import { isPbrProtected } from '../../constants/pbr';
import { TagSwatch } from './TagSwatch';

export interface OrderLineTableProps {
  lines: NurseryOrderLine[];
  onAllocate?: (lineId: number) => Promise<void>;
}

/**
 * What stock a line sold under an umbrella product drew on — or, before
 * anything is allocated, which stock item it will be filled from first.
 */
function productFill(line: NurseryOrderLine): string {
  const filled = line.filled_from ?? [];
  if (filled.length > 0) {
    return filled
      .map((f) => `${[f.cultivar_name, f.rootstock_name].filter(Boolean).join(' / ')} × ${f.quantity}`)
      .join(', ');
  }
  return `First choice: ${[line.cultivar_name, line.rootstock_name].filter(Boolean).join(' / ')}`;
}

export function OrderLineTable({ lines, onAllocate }: OrderLineTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200">
            <th className="text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide py-2 pr-3">Item</th>
            <th className="text-right text-xs font-semibold text-neutral-500 uppercase tracking-wide py-2 px-3">Ordered</th>
            <th className="text-right text-xs font-semibold text-neutral-500 uppercase tracking-wide py-2 px-3">Allocated</th>
            <th className="text-right text-xs font-semibold text-neutral-500 uppercase tracking-wide py-2 px-3">Unit $</th>
            <th className="text-right text-xs font-semibold text-neutral-500 uppercase tracking-wide py-2 pl-3">Total</th>
            {onAllocate && <th className="py-2 pl-3" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {lines.map(line => {
            // The name, not the code: codes are not unique ("20CM" is both a
            // bareroot and a pot), and the two do not allocate from the same lots.
            const itemLabel = [line.cultivar_name, line.rootstock_name, line.tree_type_name || line.tree_type_code]
              .filter(Boolean).join(' / ');
            return (
              <tr key={line.id}>
                <td className="py-2 pr-3 text-neutral-800">
                  {line.product_name ? (
                    <>
                      <span className="inline-flex items-center gap-1.5">
                        <TagSwatch hex={line.tag_colour_hex} name={line.tag_colour_name} />
                        {line.product_name}
                      </span>
                      {/* Part of what was bought: the same name comes in other tree types. */}
                      {(line.tree_type_name || line.tree_type_code) && (
                        <span className="block text-xs text-neutral-600">{line.tree_type_name || line.tree_type_code}</span>
                      )}
                      <span className="block text-xs text-neutral-500">{productFill(line)}</span>
                    </>
                  ) : (
                    <>
                      {itemLabel || `Item #${line.stock_item_id}`}
                      <PbrMark status={line.cultivar_protection_status} />
                    </>
                  )}
                </td>
                <td className="py-2 px-3 text-right text-neutral-700">{line.quantity_ordered}</td>
                <td className="py-2 px-3 text-right text-neutral-700">{line.quantity_allocated}</td>
                <td className="py-2 px-3 text-right text-neutral-500">
                  {line.unit_price != null ? `$${Number(line.unit_price).toFixed(2)}` : '—'}
                </td>
                <td className="py-2 pl-3 text-right text-neutral-700">
                  {line.line_total != null ? `$${Number(line.line_total).toFixed(2)}` : '—'}
                </td>
                {onAllocate && (
                  <td className="py-2 pl-3">
                    {line.quantity_allocated < line.quantity_ordered && (
                      <button
                        onClick={() => onAllocate(line.id)}
                        className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                      >
                        Allocate
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {lines.some((l) => !l.product_name && isPbrProtected(l.cultivar_protection_status)) && (
        <PbrNotice className="mt-3" />
      )}
    </div>
  );
}
